import { useMemo, useState } from "react";
import { useJourneyCampaigns } from "./journey/useJourneyCampaigns";
import { CampaignBadgeRow } from "./journey/CampaignBadgeRow";
import { MonthCampaignPanel } from "./journey/MonthCampaignPanel";
import { SPIKE_PCT, SPIKE_MIN_PRIOR } from "./journey/journeyChannels";
import { computeLaunches } from "./journey/journeyStory";
import { JourneyNote } from "./journey/JourneyNote";
import { useAgencyNote } from "./useAgencyNote";
import { Flag } from "lucide-react";

/**
 * BrandJourneyChart — full-width stacked column chart showing every
 * month of uploaded GA4 history for a client, broken out by channel
 * (Direct / Organic Search / Paid / Other). Designed to feel ALIVE —
 * rich color depth, animated reveal, hoverable per-month tooltips,
 * and a lime "Peak" badge that crowns the best month.
 *
 * Design rules (keep them):
 *  - Pure celebration. NO comparison bars, NO red/down indicators,
 *    NO "vs. last quarter" framing.
 *  - Trim leading zero months so the chart starts at first data.
 *  - Animated bar grow on first paint to make activity feel "live".
 *
 * Input shape:
 *  - data: [{ month: "2026-01", sessions, direct, organic, paid, other }]
 */

const CHANNELS = [
  {
    key: "direct",
    label: "Direct",
    color: "#0ea5e9",  // sky
    body: 'Indicates strong brand awareness. Includes most paid ad traffic, since GA4 often misattributes paid ad clicks as "Direct."',
  },
  {
    key: "organic",
    label: "Organic Search",
    color: "#34d399",  // emerald
    body: "Compounds over time with brand awareness. Rankings can shift with algorithm updates.",
  },
  {
    key: "other",
    label: "Other",
    color: "#a78bfa",  // violet
    body: "Word-of-mouth and earned media. Harder to attribute. Often inconsistent month-to-month.",
  },
];

const fmtTotal = (n) => {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return Math.round(n).toLocaleString();
};

const fmtMonthLabel = (m) => {
  const [y, mm] = m.split("-");
  const names = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${names[parseInt(mm, 10) - 1]} ${y.slice(2)}`;
};

// Subtitle range — uses full 4-digit year and collapses same-year ranges
// to avoid the ambiguous "Jan 26 — Mar 26" reading ("26" being mistaken
// for day-of-month). e.g. "January – March 2026" or "August 2025 – March 2026".
const fmtRangeLong = (firstMonth, lastMonth) => {
  const names = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const [y1, m1] = firstMonth.split("-");
  const [y2, m2] = lastMonth.split("-");
  const n1 = names[parseInt(m1, 10) - 1];
  const n2 = names[parseInt(m2, 10) - 1];
  if (y1 === y2) return `${n1} – ${n2} ${y1}`;
  return `${n1} ${y1} – ${n2} ${y2}`;
};

const fmtDuration = (secs) => {
  if (!secs || secs <= 0) return "-";
  const s = Math.round(secs);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r === 0 ? `${m}m` : `${m}m ${r}s`;
};

// Small lime "▲ +12% · 6mo" pill rendered next to a KPI value when the
// metric has grown vs the prior equal-length window. We never render
// negative deltas — agency requested purely celebratory framing.
const TrendPill = ({ pct, periods }) => {
  const display = pct >= 100 ? `+${Math.round(pct)}%` : `+${pct.toFixed(pct >= 10 ? 0 : 1)}%`;
  return (
    <span
      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 text-[10px] font-bold tabular-nums tracking-tight"
      title={`Up ${display} vs prior ${periods} ${periods === 1 ? "month" : "months"}`}
      data-testid="brand-journey-trend-pill"
    >
      <span className="text-[8px]">▲</span>
      {display}
    </span>
  );
};

// Compact "+12% visits" pill used in the YTD strip. Lime for growth,
// muted amber for decline. Hides itself if `delta` is null/NaN.
const YtdStat = ({ label, delta }) => {
  if (delta == null || isNaN(delta) || Math.abs(delta) < 0.5) return null;
  const up = delta > 0;
  const cls = up
    ? "text-lime-300"
    : "text-amber-300";
  const arrow = up ? "▲" : "▼";
  const display = Math.abs(delta) >= 100
    ? `${up ? "+" : "−"}${Math.round(Math.abs(delta))}%`
    : `${up ? "+" : "−"}${Math.abs(delta).toFixed(Math.abs(delta) >= 10 ? 0 : 1)}%`;
  return (
    <span className="inline-flex items-baseline gap-1.5 text-[12px]">
      <span className={`${cls} font-bold tabular-nums`}>
        <span className="text-[9px] mr-0.5">{arrow}</span>{display}
      </span>
      <span className="text-slate-300">{label}</span>
    </span>
  );
};

const MONTHS_LONG = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const fmtMonthLong = (m) => {
  const [y, mm] = m.split("-");
  return `${MONTHS_LONG[parseInt(mm, 10) - 1]} ${y}`;
};

// Smooth path through points (Catmull-Rom to cubic Bezier)
const smoothPath = (pts) => {
  let d = `M${pts[0][0]},${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    const c1 = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
    const c2 = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];
    d += ` C${c1[0]},${c1[1]} ${c2[0]},${c2[1]} ${p2[0]},${p2[1]}`;
  }
  return d;
};

export const BrandJourneyChart = ({ data = [], clientName = "Your business", clientId, API, readOnly = false, reportStart, reportEnd }) => {
  const [hover, setHover] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const journey = useJourneyCampaigns(API, clientId);
  const [journeyNote, saveJourneyNote] = useAgencyNote(API, clientId, reportStart, reportEnd, "journey");
  const campaignsFor = (m) => (journey.months[m] || []).filter((c) => !c.hidden);

  const view = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return null;

    // Merge Paid into Direct at the row level. GA4 frequently misattributes
    // paid ad clicks as Direct anyway, so showing them as separate buckets is
    // misleading. Combining them produces a more honest read. Downstream
    // rendering (bars, totals, tooltip, Top Driver) then "just works" since
    // CHANNELS no longer lists "paid".
    const merged = data.map((d) => ({
      ...d,
      direct: (d.direct || 0) + (d.paid || 0),
      paid: 0,
    }));

    // Trim leading + trailing zero months
    const firstIdx = merged.findIndex((d) => (d.sessions || 0) > 0);
    let lastIdx = -1;
    for (let i = merged.length - 1; i >= 0; i--) {
      if ((merged[i].sessions || 0) > 0) { lastIdx = i; break; }
    }
    if (firstIdx === -1 || lastIdx === -1) return null;
    const series = merged.slice(firstIdx, lastIdx + 1);
    if (series.length === 0) return null;

    const maxSessions = Math.max(...series.map((s) => s.sessions || 1), 1);
    const totalSessions = series.reduce((a, b) => a + (b.sessions || 0), 0);
    const totalEngaged = series.reduce((a, b) => a + (b.engaged_sessions || 0), 0);
    const totalEvents = series.reduce((a, b) => a + (b.event_count || 0), 0);
    const totalKeyEvents = series.reduce((a, b) => a + (b.key_events || 0), 0);
    // Session-weighted averages across the whole window (mirrors backend per-month weighting)
    const engTimeWeighted = series.reduce(
      (a, b) => a + (b.avg_engagement_time || 0) * (b.sessions || 0),
      0,
    );
    const epsWeighted = series.reduce(
      (a, b) => a + (b.events_per_session || 0) * (b.sessions || 0),
      0,
    );
    const avgEngagementTime = totalSessions > 0 ? engTimeWeighted / totalSessions : 0;
    const eventsPerSession = totalSessions > 0 ? epsWeighted / totalSessions : 0;
    const engagementRatePct = totalSessions > 0 ? (totalEngaged / totalSessions) * 100 : 0;

    // Find peak month so we can crown it
    let peakIdx = 0;
    for (let i = 1; i < series.length; i++) {
      if ((series[i].sessions || 0) > (series[peakIdx].sessions || 0)) peakIdx = i;
    }
    const peakRow = series[peakIdx];
    // Top channel across the window (drives the small "Top driver" callout in the KPI strip).
    // Only consider channels that actually render — paid has been folded into direct above.
    const channelTotals = { direct: 0, organic: 0, other: 0 };
    for (const r of series) {
      channelTotals.direct  += r.direct  || 0;
      channelTotals.organic += r.organic || 0;
      channelTotals.other   += r.other   || 0;
    }
    const topChannelKey = CHANNELS.reduce(
      (best, ch) => (channelTotals[ch.key] > (channelTotals[best] || 0) ? ch.key : best),
      "direct",
    );

    // --- Comparisons ---
    // Per agency direction (Feb 2026): callouts and KPI trend pills are
    // now month-over-month (most recent month vs. prior month), and a
    // small "Since Jan 1" YTD headline strip sits above the callouts to
    // show calendar-year trajectory at a glance.
    //
    // Compatibility note: `trends` (the lime ▲ pills next to KPI tiles)
    // keeps the same shape it had under the old half-vs-half logic so
    // the existing render code below works unchanged.
    let trends = null;
    let journeyCallouts = { ups: [], downs: [] };
    let ytdStrip = null;

    const sumOf = (arr, key) => arr.reduce((a, b) => a + (b[key] || 0), 0);
    const weightedAvg = (arr, key) => {
      const s = sumOf(arr, "sessions");
      return s > 0 ? arr.reduce((a, b) => a + (b[key] || 0) * (b.sessions || 0), 0) / s : 0;
    };

    // ---------- MoM (most recent month vs. prior month) ----------
    if (series.length >= 2) {
      const prior = series.slice(-2, -1);
      const latest = series.slice(-1);

      const calcPos = (priorVal, latestVal) => {
        if (priorVal <= 0) return null;
        const pct = ((latestVal - priorVal) / priorVal) * 100;
        if (pct <= 0) return null;
        return { pct, periods: 1 };
      };
      trends = {
        engaged: calcPos(sumOf(prior, "engaged_sessions"), sumOf(latest, "engaged_sessions")),
        engagementTime: calcPos(weightedAvg(prior, "avg_engagement_time"), weightedAvg(latest, "avg_engagement_time")),
        eventsPerSession: calcPos(weightedAvg(prior, "events_per_session"), weightedAvg(latest, "events_per_session")),
      };

      // Month-over-month callout rules.
      //  - Minimum-sample floors raised vs. half-vs-half because a single
      //    month is noisier — we want at least ~200 sessions before flagging
      //    a session-bucket move, etc.
      //  - Down side only fires at ≤ -15% so amber stays rare and earned.
      const DOWN_THRESHOLD_PCT = 15;
      const MIN_SAMPLES = {
        sessions: 200,
        direct: 150,
        organic: 150,
        other: 80,
        engaged_sessions: 150,
      };
      const buildCalcs = (key) => {
        const p = sumOf(prior, key);
        const l = sumOf(latest, key);
        const floor = MIN_SAMPLES[key] || 100;
        if (p < floor && l < floor) return null;
        if (p === 0) return l > 0 ? { isNew: true } : null;
        const pct = ((l - p) / p) * 100;
        return { pct, prior: p, latest: l };
      };
      const buildAvg = (key, minAbsDelta = 0) => {
        const p = weightedAvg(prior, key);
        const l = weightedAvg(latest, key);
        if (p === 0) return l > 0 ? { isNew: true } : null;
        if (Math.abs(l - p) < minAbsDelta) return null;
        const pct = ((l - p) / p) * 100;
        return { pct, prior: p, latest: l, absDelta: l - p };
      };
      const candidates = [
        { key: "sessions",         label: "Website visits",          c: buildCalcs("sessions") },
        { key: "direct",           label: "Direct visits",           c: buildCalcs("direct") },
        { key: "organic",          label: "Organic Search",          c: buildCalcs("organic") },
        { key: "other",            label: "Other-source visits",     c: buildCalcs("other") },
        { key: "engaged_sessions", label: "Engaged visits",          c: buildCalcs("engaged_sessions") },
        { key: "avg_engagement_time", label: "Time on site",         c: buildAvg("avg_engagement_time", 5) },
        { key: "events_per_session",  label: "Events per visit",     c: buildAvg("events_per_session", 0.3) },
      ];

      const engagementKeys = ["avg_engagement_time", "events_per_session"];
      const ups = [
        ...candidates
          .filter((m) => !engagementKeys.includes(m.key) && m.c && !m.c.isNew && m.c.pct > 5)
          .sort((a, b) => b.c.pct - a.c.pct)
          .slice(0, 3),
        ...[
          { key: "avg_engagement_time", label: "Time on site", c: buildAvg("avg_engagement_time") },
          { key: "events_per_session", label: "Events per visit", c: buildAvg("events_per_session") },
        ].filter((m) => m.c && !m.c.isNew && m.c.pct > 0),
      ];
      const downs = candidates
        .filter((m) => m.c && !m.c.isNew && m.c.pct <= -DOWN_THRESHOLD_PCT)
        .sort((a, b) => a.c.pct - b.c.pct)
        .slice(0, 2);
      journeyCallouts = {
        ups, downs,
        priorLabel: prior[0]?.month ? fmtMonthLabel(prior[0].month) : "",
        compareLabel: prior[0]?.month && latest[0]?.month ? `${fmtMonthLong(latest[0].month)} vs. ${fmtMonthLong(prior[0].month)}` : "",
        latestLabel: latest[0]?.month ? fmtMonthLabel(latest[0].month) : "",
      };
    }

    // ---------- YTD ("Since Jan 1" of current calendar year) ----------
    // Pulls every series row whose month starts with the current year.
    // Compares the most recent month within that year to January of the
    // same year. Hides the strip if January data is missing, the year
    // just started (no prior month yet), or volumes are too thin to
    // report responsibly.
    const latestRow = series[series.length - 1];
    if (latestRow && latestRow.month) {
      const [latestYear] = latestRow.month.split("-");
      const ytdRows = series.filter((r) => r.month && r.month.startsWith(`${latestYear}-`));
      const janRow = ytdRows.find((r) => r.month === `${latestYear}-01`);
      // Require: January present, latest is NOT January, and at least 2 months
      if (janRow && janRow.month !== latestRow.month && ytdRows.length >= 2) {
        const ytdLatest = ytdRows[ytdRows.length - 1];
        const calc = (key, floor = 100) => {
          const p = janRow[key] || 0;
          const l = ytdLatest[key] || 0;
          if (p < floor) return null;
          if (p === 0) return null;
          const pct = ((l - p) / p) * 100;
          return { pct, prior: p, latest: l };
        };
        const calcAvg = (key, minAbsDelta = 0) => {
          const p = janRow[key] || 0;
          const l = ytdLatest[key] || 0;
          if (p === 0) return null;
          if (Math.abs(l - p) < minAbsDelta) return null;
          const pct = ((l - p) / p) * 100;
          return { pct };
        };
        const ytdSessions = calc("sessions", 200);
        const ytdEngaged = calc("engaged_sessions", 150);
        const ytdTime = calcAvg("avg_engagement_time", 5);
        // Only build the strip if at least one signal cleared its floor
        if (ytdSessions || ytdEngaged || ytdTime) {
          ytdStrip = {
            year: latestYear,
            latestLabel: fmtMonthLabel(ytdLatest.month),
            sessions: ytdSessions,
            engaged: ytdEngaged,
            time: ytdTime,
          };
        }
      }
    }

    // Spike months: visits jumped meaningfully vs the prior month
    const spikes = {};
    for (let i = 1; i < series.length; i++) {
      const p = series[i - 1].sessions || 0;
      const l = series[i].sessions || 0;
      if (p >= SPIKE_MIN_PRIOR && ((l - p) / p) * 100 >= SPIKE_PCT) {
        spikes[series[i].month] = ((l - p) / p) * 100;
      }
    }

    return {
      series,
      spikes,
      maxSessions,
      totalSessions,
      totalEngaged,
      totalEvents,
      totalKeyEvents,
      avgEngagementTime,
      eventsPerSession,
      engagementRatePct,
      channelTotals,
      topChannelKey,
      trends,
      journeyCallouts,
      ytdStrip,
      firstLabel: fmtMonthLabel(series[0].month),
      lastLabel: fmtMonthLabel(series[series.length - 1].month),
      rangeLabel: fmtRangeLong(series[0].month, series[series.length - 1].month),
      peakIdx,
      peakSessions: peakRow.sessions || 0,
      peakLabel: fmtMonthLabel(peakRow.month),
    };
  }, [data]);

  if (!view) return null;

  const {
    series, maxSessions, totalSessions, totalEngaged, totalEvents, totalKeyEvents,
    avgEngagementTime, eventsPerSession, engagementRatePct,
    channelTotals, topChannelKey, trends, journeyCallouts, ytdStrip,
    rangeLabel, peakIdx, peakSessions, peakLabel, spikes,
  } = view;
  const spikeSet = new Set(Object.keys(spikes));
  const launches = computeLaunches(series, journey.months);
  const topChannel = CHANNELS.find((c) => c.key === topChannelKey) || CHANNELS[0];
  const topChannelPct = totalSessions > 0
    ? Math.round(((channelTotals[topChannelKey] || 0) / totalSessions) * 100)
    : 0;

  // Layout
  const W = 1000;
  const H = 300;
  const padL = 36;
  const padR = 8;
  const padT = 40;
  const padB = 28;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const barCount = series.length;
  const barSlot = innerW / barCount;
  // Fatter bars — more visual presence (fills empty space between columns)
  const barW = Math.max(4, Math.min(barSlot * 0.85, 64));

  const trendPts = series.map((row, i) => [
    padL + i * barSlot + barSlot / 2,
    padT + innerH - ((row.sessions || 0) / maxSessions) * innerH,
  ]);
  const selIdx = series.findIndex((r) => r.month === selectedMonth);

  // Y ticks (3 lines: 0, mid, max)
  const tickValues = [0, Math.round(maxSessions / 2), maxSessions];

  // Build segments for a single bar (bottom-to-top stack: direct → organic → paid → other)
  const renderBar = (row, i) => {
    const cx = padL + i * barSlot + (barSlot - barW) / 2;
    let cursorY = padT + innerH; // start at bottom (highest Y in svg coords)
    const segs = [];
    for (const ch of CHANNELS) {
      const v = row[ch.key] || 0;
      if (v <= 0) continue;
      const h = (v / maxSessions) * innerH;
      cursorY -= h;
      segs.push(
        <rect
          key={ch.key}
          x={cx}
          y={cursorY}
          width={barW}
          height={h}
          fill={`url(#bj-${ch.key})`}
          rx={Math.min(1.5, barW / 4)}
          className="bj-bar-seg"
          style={{ animationDelay: `${i * 8}ms` }}
        />
      );
    }
    // Hover hotspot covering full column
    segs.push(
      <rect
        key="hot"
        x={padL + i * barSlot}
        y={padT}
        width={barSlot}
        height={innerH}
        fill="transparent"
        onMouseEnter={(e) => {
          const svg = e.currentTarget.ownerSVGElement;
          const rect = svg.getBoundingClientRect();
          const containerRect = svg.parentElement.getBoundingClientRect();
          // Position tooltip relative to chart container
          const xPct = ((cx + barW / 2) / W) * rect.width;
          setHover({
            i,
            row,
            x: xPct,
            w: rect.width,
            bw: (barW / W) * rect.width,
            label: fmtMonthLabel(row.month),
          });
        }}
        onMouseLeave={() => setHover(null)}
      />
    );
    return <g key={i}>{segs}</g>;
  };

  // Decimate x-axis labels so they don't overlap
  const maxLabels = 12;
  const labelEvery = Math.max(1, Math.ceil(barCount / maxLabels));

  return (
    <div
      className="relative bg-gradient-to-br from-emerald-500/10 via-teal-500/[0.06] to-sky-500/[0.08] border border-emerald-400/25 rounded-2xl p-5 md:p-6 mb-6 overflow-hidden"
      data-testid="brand-journey-chart"
    >
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <p className="text-emerald-200 text-[11px] font-bold uppercase tracking-[0.18em] mb-1">
            The Brand's Journey
          </p>
          <p className="text-white text-base md:text-lg font-bold leading-tight">
            Your Google Analytics: {fmtTotal(totalSessions)} website visits, and counting.
          </p>
          <p className="text-slate-300 text-xs md:text-sm mt-1.5">
            <span className="font-semibold text-slate-200">{rangeLabel}</span>
            <span className="text-slate-500 mx-2">·</span>
            <span className="tabular-nums">{barCount} {barCount === 1 ? "month" : "months"} of history</span>
          </p>
        </div>
      </div>

      {/* Chart */}
      <div className="relative">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          className="w-full h-64 md:h-72 select-none"
        >
          <defs>
            {CHANNELS.map((ch) => (
              <linearGradient key={ch.key} id={`bj-${ch.key}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={ch.color} stopOpacity="1" />
                <stop offset="100%" stopColor={ch.color} stopOpacity="0.55" />
              </linearGradient>
            ))}
          </defs>

          {/* Y grid + labels */}
          {tickValues.map((tv, idx) => {
            const y = padT + innerH - (tv / maxSessions) * innerH;
            return (
              <g key={idx}>
                <line
                  x1={padL}
                  x2={W - padR}
                  y1={y}
                  y2={y}
                  stroke="#94a3b8"
                  strokeOpacity={idx === 0 ? 0.35 : 0.12}
                  strokeWidth="1"
                  vectorEffect="non-scaling-stroke"
                />
                <text
                  x={padL - 6}
                  y={y + 4}
                  textAnchor="end"
                  fontSize="11"
                  fill="#94a3b8"
                  fontFamily="ui-sans-serif, system-ui"
                >
                  {fmtTotal(tv)}
                </text>
              </g>
            );
          })}

          {/* Bars */}
          {series.map((row, i) => renderBar(row, i))}

          {/* Trend line: total website visits month to month */}
          {trendPts.length > 1 && (
            <>
              <clipPath id="bj-trend-clip">
                <rect x="0" y="0" width={W} height={H}>
                  <animate attributeName="width" from="0" to={W} dur="1.4s" begin="0.3s" fill="freeze" />
                </rect>
              </clipPath>
              <path
                d={smoothPath(trendPts)}
                fill="none"
                stroke="#d9f99d"
                strokeWidth="2.5"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
                clipPath="url(#bj-trend-clip)"
                className="bj-trend-line"
                pointerEvents="none"
                data-testid="brand-journey-trend-line"
              />
            </>
          )}

          {/* Peak badge — crowns the highest-session bar */}
          {peakSessions > 0 && (() => {
            const peakCx = padL + peakIdx * barSlot + barSlot / 2;
            const peakBarTopY = padT + innerH - (peakSessions / maxSessions) * innerH;
            const badgeW = 130;
            const badgeH = 26;
            // Clamp so badge stays inside the svg viewBox horizontally
            const halfW = badgeW / 2;
            const cx = Math.max(padL + halfW, Math.min(W - padR - halfW, peakCx));
            // Vertical position: 12px above the bar top; if too close to chart top, drop below header padding instead
            const desiredTop = peakBarTopY - 12 - badgeH;
            const badgeTop = desiredTop < 2 ? 4 : desiredTop;
            return (
              <g style={{ animation: "bj-peak-pop 900ms 600ms cubic-bezier(0.34, 1.56, 0.64, 1) backwards" }}>
                {/* Connector line from badge bottom-center to bar top */}
                <line
                  x1={peakCx}
                  x2={peakCx}
                  y1={badgeTop + badgeH}
                  y2={peakBarTopY - 1}
                  stroke="#d9f99d"
                  strokeOpacity="0.55"
                  strokeWidth="1"
                  strokeDasharray="2 2"
                  vectorEffect="non-scaling-stroke"
                />
                {/* Tiny diamond marker on the bar top */}
                <rect
                  x={peakCx - 3}
                  y={peakBarTopY - 3}
                  width="6"
                  height="6"
                  fill="#d9f99d"
                  transform={`rotate(45, ${peakCx}, ${peakBarTopY})`}
                />
                {/* Pill background */}
                <rect
                  x={cx - halfW}
                  y={badgeTop}
                  width={badgeW}
                  height={badgeH}
                  rx="13"
                  fill="#0a3d54"
                  stroke="#d9f99d"
                  strokeOpacity="0.75"
                  strokeWidth="1"
                  vectorEffect="non-scaling-stroke"
                />
                {/* Star/sparkle icon on the left */}
                <text
                  x={cx - halfW + 11}
                  y={badgeTop + badgeH / 2 + 4.5}
                  fontSize="12"
                  fill="#d9f99d"
                  fontFamily="ui-sans-serif, system-ui"
                  fontWeight="700"
                >
                  ★
                </text>
                {/* "PEAK" eyebrow */}
                <text
                  x={cx - halfW + 24}
                  y={badgeTop + badgeH / 2 + 4.5}
                  fontSize="9"
                  fill="#d9f99d"
                  fontFamily="ui-sans-serif, system-ui"
                  fontWeight="800"
                  letterSpacing="1.2"
                >
                  PEAK
                </text>
                {/* Value + date */}
                <text
                  x={cx - halfW + 54}
                  y={badgeTop + badgeH / 2 + 4.5}
                  fontSize="11"
                  fill="#ffffff"
                  fontFamily="ui-sans-serif, system-ui"
                  fontWeight="700"
                >
                  {`${fmtTotal(peakSessions)} · ${peakLabel}`}
                </text>
              </g>
            );
          })()}

          {/* X labels */}
          {series.map((row, i) => {
            if (i % labelEvery !== 0 && i !== barCount - 1) return null;
            const cx = padL + i * barSlot + barSlot / 2;
            const label = fmtMonthLabel(row.month);
            return (
              <text
                key={`xl-${i}`}
                x={cx}
                y={H - 8}
                textAnchor="middle"
                fontSize="10"
                fill="#94a3b8"
                fontFamily="ui-sans-serif, system-ui"
              >
                {label}
              </text>
            );
          })}

          {/* Hover guide line */}
          {hover && (
            <line
              x1={padL + hover.i * barSlot + barSlot / 2}
              x2={padL + hover.i * barSlot + barSlot / 2}
              y1={padT}
              y2={padT + innerH}
              stroke="#34d399"
              strokeOpacity="0.5"
              strokeDasharray="2 3"
              vectorEffect="non-scaling-stroke"
            />
          )}
        </svg>

        {/* Trend dots (HTML so they stay round on the stretched SVG) */}
        {trendPts.map(([x, y], i) => {
          const m = series[i].month;
          const spike = spikeSet.has(m);
          return (
            <span
              key={`td-${m}`}
              className={`absolute -translate-x-1/2 -translate-y-1/2 pointer-events-none rounded-full ${
                spike ? "w-3.5 h-3.5 bg-lime-300 ring-4 ring-lime-300/25 bj-spike-dot" : "w-2 h-2 bg-lime-100 ring-2 ring-[#0a3d54]"
              }`}
              style={{ left: `${(x / W) * 100}%`, top: `${(y / H) * 100}%` }}
              data-testid={spike ? `brand-journey-spike-dot-${m}` : `brand-journey-trend-dot-${m}`}
            />
          );
        })}

        {/* Launch flags: campaigns that started this month */}
        {series.map((row, i) => {
          const fresh = launches[row.month];
          if (!fresh) return null;
          return (
            <span
              key={`lf-${row.month}`}
              className="absolute z-10 inline-flex items-center gap-0.5 rounded-full bg-[#0a3d54] ring-1 ring-sky-300/60 text-sky-200 px-1.5 py-0.5 text-[9px] font-bold pointer-events-none"
              style={{ left: `${((padL + i * barSlot + barSlot / 2) / W) * 100}%`, top: `${((padT + innerH) / H) * 100}%`, transform: "translate(-50%, -150%)" }}
              data-testid={`brand-journey-launch-flag-${row.month}`}
            >
              <Flag className="w-2.5 h-2.5" />
              <span className="tabular-nums">{fresh.length}</span>
              <span className="hidden md:inline">new</span>
            </span>
          );
        })}

        {/* Tooltip */}
        {hover && (
          <div
            className="absolute z-30 pointer-events-none bg-slate-900/95 backdrop-blur border border-emerald-400/40 rounded-lg px-3 py-2 text-[11px] shadow-2xl whitespace-nowrap"
            style={{
              left: `${hover.w < 600 ? hover.w / 2 : hover.x}px`,
              top: "8px",
              transform: hover.w < 600 ? "translateX(-50%)" : hover.x > hover.w / 2 ? `translateX(calc(-100% - ${hover.bw / 2 + 10}px))` : `translateX(${hover.bw / 2 + 10}px)`,
            }}
            data-testid="brand-journey-tooltip"
          >
            <p className="text-emerald-300 font-bold mb-1">{hover.label}</p>
            <p className="text-white font-bold mb-1.5">
              {fmtTotal(hover.row.sessions || 0)} visits
            </p>
            {CHANNELS.map((ch) => {
              const v = hover.row[ch.key] || 0;
              if (v <= 0) return null;
              return (
                <p key={ch.key} className="text-slate-300 flex items-center gap-1.5">
                  <span className="inline-block w-2 h-2 rounded-sm" style={{ background: ch.color }} />
                  <span className="text-slate-400">{ch.label}:</span>
                  <span className="text-white font-semibold tabular-nums">{fmtTotal(v)}</span>
                </p>
              );
            })}
            {(hover.row.engaged_sessions > 0 || hover.row.avg_engagement_time > 0) && (
              <div className="mt-1.5 pt-1.5 border-t border-slate-700/60 text-slate-400 text-[10px] space-y-0.5">
                {hover.row.engaged_sessions > 0 && hover.row.sessions > 0 && (
                  <p className="tabular-nums">
                    Engaged: {Math.round((hover.row.engaged_sessions / hover.row.sessions) * 100)}%
                  </p>
                )}
                {hover.row.avg_engagement_time > 0 && (
                  <p className="tabular-nums">Avg time: {fmtDuration(hover.row.avg_engagement_time)}</p>
                )}
              </div>
            )}
            {spikes[hover.row.month] != null && (
              <p className="mt-1.5 text-lime-300 font-semibold">Visits up {Math.round(spikes[hover.row.month])}% vs prior month</p>
            )}
            {launches[hover.row.month] && (
              <p className="mt-1.5 text-sky-200 font-semibold flex items-center gap-1" data-testid="brand-journey-tooltip-launches">
                <Flag className="w-3 h-3" /> {launches[hover.row.month].length} new {launches[hover.row.month].length === 1 ? "campaign" : "campaigns"} launched
              </p>
            )}
            {campaignsFor(hover.row.month).length > 0 && (
              <div className="mt-1.5 pt-1.5 border-t border-slate-700/60 space-y-0.5" data-testid="brand-journey-tooltip-campaigns">
                <p className="text-slate-400 text-[10px] uppercase tracking-wider font-semibold">Paid campaigns running</p>
                {campaignsFor(hover.row.month).slice(0, 4).map((c) => (
                  <p key={c.key} className="flex items-center gap-1.5 text-slate-200 max-w-[240px]">
                    <span className="inline-block w-1.5 h-1.5 rounded-full shrink-0 bg-slate-400" />
                    <span className="truncate">{c.name}</span>
                  </p>
                ))}
                {campaignsFor(hover.row.month).length > 4 && (
                  <p className="text-slate-500 text-[10px]">+{campaignsFor(hover.row.month).length - 4} more, tap the month below</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Paid campaigns under each month */}
      <CampaignBadgeRow
        series={series}
        campaignsFor={campaignsFor}
        spikeSet={spikeSet}
        selected={selectedMonth}
        onSelect={setSelectedMonth}
        padLPct={(padL / W) * 100}
        padRPct={(padR / W) * 100}
      />
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-[10px] text-slate-400" data-testid="brand-journey-trend-key">
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-4 h-0.5 rounded bg-lime-200" /> Total visits trend</span>
        {spikeSet.size > 0 && (
          <span className="inline-flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full bg-lime-300" /> Big jump in visits</span>
        )}
        {Object.keys(launches).length > 0 && (
          <span className="inline-flex items-center gap-1 text-sky-200"><Flag className="w-3 h-3" /> New campaign launched</span>
        )}
        <span>Tap a month to see the paid campaigns that were running.</span>
      </div>
      <JourneyNote note={journeyNote} readOnly={readOnly} onSave={saveJourneyNote} />
      {selIdx >= 0 && (
        <MonthCampaignPanel
          month={selectedMonth}
          label={fmtMonthLong(selectedMonth)}
          visits={series[selIdx].sessions || 0}
          spikePct={spikes[selectedMonth] ?? null}
          prevLabel={selIdx > 0 ? fmtMonthLong(series[selIdx - 1].month).split(" ")[0] : ""}
          items={journey.months[selectedMonth] || []}
          newKeys={new Set((launches[selectedMonth] || []).map((c) => c.key))}
          readOnly={readOnly}
          actions={journey}
          fmtTotal={fmtTotal}
        />
      )}

      {/* Channel legend — inline color-dot legend with one-sentence explainer
          per channel. Aligns across the bottom so the card stays tight. */}
      <div
        className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-3 mt-4 pt-4 border-t border-emerald-400/10"
        data-testid="brand-journey-channel-guide"
      >
        {CHANNELS.map((ch) => {
          const v = channelTotals[ch.key] || 0;
          const pct = totalSessions > 0 ? Math.round((v / totalSessions) * 100) : 0;
          return (
            <div key={ch.key} className="flex gap-2 text-[11px]">
              <span
                className="inline-block w-2.5 h-2.5 rounded-sm flex-shrink-0 mt-[3px]"
                style={{ background: ch.color }}
              />
              <div className="min-w-0">
                <p className="text-white font-bold leading-snug">
                  {ch.label}
                  <span className="text-slate-500 font-normal tabular-nums ml-1.5">
                    · {fmtTotal(v)} · {pct}%
                  </span>
                </p>
                <p className="text-slate-400 leading-snug mt-0.5">{ch.body}</p>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-5">
      {/* Journey callouts — celebratory "What's Working" + rare amber
          "Worth a Conversation" surface the most recent month vs. prior
          month. Down-side only fires when the drop is >=15% so amber
          stays rare and earned. */}
      {(journeyCallouts.ups.length > 0 || journeyCallouts.downs.length > 0) && (
        <>
        <div className="grid grid-cols-1 gap-3 mb-5" data-testid="brand-journey-callouts">
          {journeyCallouts.ups.length > 0 && (
            <div
              className="rounded-xl bg-lime-500/8 border border-lime-400/30 p-3.5"
              data-testid="brand-journey-callout-working"
            >
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 mb-2">
                <p className="text-lime-300 text-[10px] font-bold uppercase tracking-[0.18em] flex items-center gap-1.5">
                  <span className="text-base leading-none">🌱</span> What&apos;s Working
                </p>
                {journeyCallouts.compareLabel && (
                  <p className="text-slate-300 text-[11px]" data-testid="brand-journey-callout-working-dates">{journeyCallouts.compareLabel}</p>
                )}
              </div>
              <ul className="flex flex-wrap gap-x-6 gap-y-1.5 text-[13px] text-slate-200 leading-snug">
                {journeyCallouts.ups.map((m, i) => (
                  <li key={i} className="flex items-center gap-1.5">
                    <span className="text-lime-400 font-bold">▲</span>
                    <span>
                      <span className="text-white font-semibold">{m.label}</span>
                      <span className="text-lime-300 font-bold ml-1.5 tabular-nums">
                        +{m.c.pct >= 100 ? Math.round(m.c.pct) : m.c.pct.toFixed(m.c.pct >= 10 ? 0 : 1)}%
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {journeyCallouts.downs.length > 0 && (
            <div
              className="rounded-xl bg-amber-500/8 border border-amber-400/25 p-3.5"
              data-testid="brand-journey-callout-conversation"
            >
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 mb-2">
                <p className="text-amber-300 text-[10px] font-bold uppercase tracking-[0.18em] flex items-center gap-1.5">
                  <span className="text-base leading-none">💬</span> Worth a Conversation
                </p>
                {journeyCallouts.compareLabel && (
                  <p className="text-slate-300 text-[11px]">{journeyCallouts.compareLabel}</p>
                )}
              </div>
              <ul className="flex flex-wrap gap-x-6 gap-y-1.5 text-[13px] text-slate-200 leading-snug">
                {journeyCallouts.downs.map((m, i) => (
                  <li key={i} className="flex items-center gap-1.5">
                    <span className="text-amber-400 font-bold">▼</span>
                    <span>
                      <span className="text-white font-semibold">{m.label}</span>
                      <span className="text-amber-300 font-bold ml-1.5 tabular-nums">
                        {Math.round(m.c.pct)}%
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
              <p className="text-amber-200/70 text-[10px] italic mt-2 leading-snug">
                Not a problem, just a data point. Your ad rep can dig into the why.
              </p>
            </div>
          )}
        </div>
        </>
      )}

      </div>

      {/* Disclaimer footnote */}
      <p
        className="mt-4 pt-3 border-t border-emerald-400/10 text-slate-400 text-[11px] italic leading-relaxed"
        data-testid="brand-journey-footnote"
      >
        This snapshot is meant to tell the overall story of {clientName}'s brand
        traffic, not a deep performance audit. For a detailed read on what these
        numbers mean for your business, talk to your advertising rep.
      </p>

      <style>{`
        .bj-bar-seg {
          transform-origin: bottom;
          animation: bj-grow 700ms cubic-bezier(0.22, 1, 0.36, 1) backwards;
        }
        @keyframes bj-grow {
          from { transform: scaleY(0); opacity: 0.2; }
          to   { transform: scaleY(1); opacity: 1; }
        }
        .bj-trend-line { filter: drop-shadow(0 0 4px rgba(217,249,157,0.45)); }
        .bj-spike-dot { animation: bj-pulse 2.2s ease-in-out infinite; }
        @keyframes bj-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(190,242,100,0.45); }
          50% { box-shadow: 0 0 0 8px rgba(190,242,100,0); }
        }
        @keyframes bj-peak-pop {
          from { transform: translateY(6px) scale(0.6); opacity: 0; }
          to   { transform: translateY(0)   scale(1);   opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default BrandJourneyChart;
